import React, { useEffect, useState } from 'react';

// Hooks
import { useDebounce } from '../../hooks/useDebounce';

// Store
import { Cycle } from '../../store/models/cycles';

// Types
import { SearchCycle as SearchCycleQuery } from '../../types/search-name';

// Components
import Modal from '../Modal/Modal';
import InputControl from '../InputControl/InputControl';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';

type ComponentProps = {
  visibility: boolean;
  onClose: () => void;
  getCycles: (query?: SearchCycleQuery) => Promise<Cycle[]>;
  cyclesBySearch: Cycle[];
  onCLickCycle: (cycle: Cycle) => void | Promise<void>;
  btnText?: string
};

const SearchCycle: React.FC<ComponentProps> = ({
  visibility,
  onClose,
  getCycles,
  cyclesBySearch,
  onCLickCycle,
  btnText,
}) => {
  const [allCycles, setAllCycles] = useState<Cycle[] | null>(null);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);

  const debouncedSearchQuery = useDebounce<string>(searchQuery, 300);

  const updateSearchText = (event) => {
    const { value } = event.currentTarget;
    setSearchQuery(value);
  };

  const selectCycle = async (cycle: Cycle) => {
    onCLickCycle(cycle);
    await firstLoad();
  }

  const clickItemList = async (cycle: Cycle) => {
    if (btnText) {
      return;
    }

    await selectCycle(cycle);
  }

  const firstLoad = async () => {
    const response = await getCycles();
    setAllCycles(response);
  };

  const searchCyclesByName = async (name: string) => {
    const query: SearchCycleQuery = { name };
    await getCycles(query);
  };

  const getListCycles = (query: string) => {
    if (query && query.length > 2) {
      return cyclesBySearch;
    };

    return allCycles;
  }

  useEffect(() => {
    firstLoad()
  }, []);

  useEffect(() => {
    if (debouncedSearchQuery && debouncedSearchQuery?.length > 2) {
      searchCyclesByName(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery]);

  return (
    <Modal
      isVisible={visibility}
      onClose={onClose}
      modalTitle='Choose cycles'
      showAcceptBtn={false}
      showCancelBtn={false}
    >
      <div className="c-cycle-search-screen h-full w-full">
        {allCycles === null && (
          <div className='h-75 w-full flex justify-center items-center'><SpinnerLoader /></div>
        )}
        {allCycles !== null && (
          <div className='h-80 flex flex-col'>
            <div className='pb-3'>
              <InputControl
                type="text"
                name="email"
                placeholder="Enter cycle name"
                value={searchQuery || ''}
                onChange={updateSearchText}
                autoComplete="off"
              />
            </div>
            <div className='c-cycle-search-screen__wrapper-list overflow-y-auto flex-auto'>
              {cyclesBySearch.length !== 0 && (
                <ul className="c-cycle-search-screen__list flex-auto flex flex-col gap-y-2 overflow-y-auto overflow-x-hidden">
                  {getListCycles(searchQuery).map((cycle) => (
                    <li
                      key={cycle.id}
                      className='grid gap-x-2 w-full items-center grid-cols-[1fr,auto]'
                      onClick={() => clickItemList(cycle)}
                    >
                      <span className='truncate text-left'>{cycle.name}</span>
                      {btnText && (
                        <InteractiveButton
                          text={btnText}
                          onClick={() => selectCycle(cycle)}
                          className='w-31'
                        />
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {cyclesBySearch.length === 0 && searchQuery && searchQuery.length > 2 && (
                <div className='flex-auto flex justify-center items-center'>
                  <span>No cycles found</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SearchCycle;
